import { t } from "../../store-language/i18n.ts";
import { HttpService } from '../../../utils/DesigningHttpService.js'
import type { ActiveShowroom, CategoryProduct, ShowroomStand } from '../types/category.ts'
import type { ShowroomSlotName } from '../types/showroom.ts'

type ActiveShowroomDto = {
  id: number
  title: string
  slots: Array<{
    id: number
    slotCode: string
    title: string
    slotType: 'Takhte' | 'Dar'
    displayOrder: number
    products: Array<{
      productVariantId: number
      masterProductId: number
      masterSlug: string
      title: string
      code: string
      thumbnailMediaId: number | null
      masterTitle?: string
      paletteValueId?: number | null
      colorValueId?: number | null
      dimensionValueId?: number | null
    }>
  }>
}

const http = new HttpService()

function isSlotName(value: string): value is ShowroomSlotName {
  return Boolean(value.trim())
}

function mapProduct(product: ActiveShowroomDto['slots'][number]['products'][number]): CategoryProduct {
  return {
    id: product.productVariantId,
    masterProductId: product.masterProductId,
    masterSlug: product.masterSlug,
    code: product.code,
    title: product.title,
    fileId: product.thumbnailMediaId ?? 0,
    schemaTitle: product.masterTitle,
    paletteId: product.paletteValueId,
    backgroundColorId: product.colorValueId,
    dimentionsId: product.dimensionValueId,
  }
}

export type ActiveShowroomContent = {
  showroom: Omit<ActiveShowroom, 'modelUrl'>
  model: Blob
  stands: ShowroomStand[]
}

export async function getActiveShowroom(signal: AbortSignal): Promise<ActiveShowroomContent | null> {
  const download = await http.getblob('/catalog/showroom/active', signal)
  const body = download.data as Blob
  if (!body || body.size === 0) return null
  // XHR Blob.type can omit MIME parameters in Firefox. The multipart boundary
  // must come from the complete response header, not the Blob's media type.
  const contentType = download.headers['content-type']
  if (typeof contentType !== 'string' || !/^multipart\/form-data\s*;/i.test(contentType)) {
    throw new Error(t("پاسخ نمایشگاه فعال معتبر نیست."))
  }
  const parts = await new Response(body, { headers: { 'Content-Type': contentType } }).formData()
  const metadata = parts.get('showroom')
  const model = parts.get('model')
  if (typeof metadata !== 'string' || !(model instanceof Blob) || model.size < 12) {
    throw new Error(t("پاسخ نمایشگاه فعال معتبر نیست."))
  }
  const signature = new Uint8Array(await model.slice(0, 4).arrayBuffer())
  if (signature[0] !== 0x67 || signature[1] !== 0x6c || signature[2] !== 0x54 || signature[3] !== 0x46) {
    throw new Error(t("پاسخ نمایشگاه فعال معتبر نیست."))
  }
  const response = JSON.parse(metadata) as ActiveShowroomDto
  if (response.id <= 0 || !response.title || !Array.isArray(response.slots)) {
    throw new Error(t("پاسخ نمایشگاه فعال معتبر نیست."))
  }

  return {
    showroom: {
      id: response.id,
      key: String(response.id),
      title: response.title,
    },
    model,
    stands: response.slots.flatMap(function (slot): ShowroomStand[] {
      if (!isSlotName(slot.slotCode)) return []
      return [{
        id: slot.id,
        slot: slot.slotCode.trim(),
        label: slot.title,
        slotType: slot.slotType,
        displayOrder: slot.displayOrder,
        products: Array.isArray(slot.products) ? slot.products.map(mapProduct) : [],
      }]
    }),
  }
}
